import { useMemo } from "react";
import { AddressPayload } from "../routes/Organizations/api/organizations.api";
import { useGetStateListQuery } from "../contexts/endpoints/shared/stateList.api";

/**
 * Hook to find the state name from a state ID for a given country
 * @param address An address object containing stateOrProvince ID and country
 * @returns The state name if found, otherwise the original stateOrProvince ID
 */
export const useStateNameLookup = (
  address?: AddressPayload
): string | undefined => {
  // Fetch states for the selected country
  const { data: stateListData } = useGetStateListQuery(
    { countryCode: address?.country || "" },
    { skip: !address?.country }
  );

  // Find the state name that matches the state ID
  return useMemo(() => {
    if (!address?.stateOrProvince) return undefined;

    const stateName = stateListData?.states?.find(
      (state) => state.id === address.stateOrProvince
    )?.name;

    return stateName || address.stateOrProvince;
  }, [stateListData, address]);
};

/**
 * Hook to get an address object with the state name instead of the state ID
 * @param address An address object containing stateOrProvince ID and country
 * @returns A new address object with the state name instead of the state ID
 */
export const useAddressWithFullStateName = <T extends AddressPayload>(
  address?: T
): T | undefined => {
  const stateName = useStateNameLookup(address);

  return useMemo(() => {
    if (!address) return undefined;

    return {
      ...address,
      stateOrProvince: stateName || address.stateOrProvince,
    };
  }, [address, stateName]);
};
